import { OpenAiReviewToolEventProperties } from "@/analytics/ai-detection-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { AI_GRADIENT } from "@/components/common/ai-assets/ai-gradients";
import {
  selectAiDetectedMarkupsCount,
  selectAiDetectedMarkupsNotReviewedCount,
  selectFirstAiDetectedMarkupNotReviewedId,
} from "@/store/ai-asset-review/ai-asset-review-data-selectors";
import {
  selectCurrentAnnotation,
  selectShowStartAssetDetection,
} from "@/store/ai-asset-review/ai-asset-review-selectors";
import { setCurrentAnnotationId } from "@/store/ai-asset-review/ai-asset-review-slice";
import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import { changeMode } from "@/store/mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectCanUseObjectDetection } from "@/store/subscription-features/subscription-features-selectors";
import { FaroButton, FaroText, grey, TranslateVar } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Stack } from "@mui/material";
import { memo, useCallback, useEffect, useState, type JSX } from "react";
import { AiStartDetection } from "./ai-start-detection";

/** @returns notification banner for AI asset review when AI-detected annotations exist, or detection prompt when none exist */
export function AiReviewNotification(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const notReviewedCount = useAppSelector(
    selectAiDetectedMarkupsNotReviewedCount,
  );
  const totalDetectionsCount = useAppSelector(selectAiDetectedMarkupsCount);
  const firstNotReviewedId = useAppSelector(
    selectFirstAiDetectedMarkupNotReviewedId,
  );
  const canUseObjectDetection = useAppSelector(selectCanUseObjectDetection);
  const currentAnnotation = useAppSelector(selectCurrentAnnotation);
  const [isExpanded, setIsExpanded] = useState(false);
  const showStartAssetDetection = useAppSelector(selectShowStartAssetDetection);
  const canRerunAssetDetection = useAppSelector(
    selectHasFeature(Features.RerunAssetDetection),
  );

  const hasCurrentAnnotation = !!currentAnnotation;

  useEffect(() => {
    setIsExpanded(hasCurrentAnnotation);
  }, [hasCurrentAnnotation]);

  const handleStartReview = useCallback(() => {
    // hasCurrentAnnotation is in when the user is in the process of the review, so we don't want to track the event
    if (!hasCurrentAnnotation) {
      Analytics.track<OpenAiReviewToolEventProperties>(
        EventType.openAiDetectionReviewTool,
        {
          totalDetectionsToReview: totalDetectionsCount,
          via: "annotation-panel",
        },
      );
    }
    if (!hasCurrentAnnotation && firstNotReviewedId) {
      dispatch(setCurrentAnnotationId(firstNotReviewedId));
    }
    dispatch(changeMode("aiAssetReview"));
  }, [
    dispatch,
    totalDetectionsCount,
    hasCurrentAnnotation,
    firstNotReviewedId,
  ]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((v) => !v);
  }, []);

  if (!canUseObjectDetection) {
    return null;
  }

  // Case 1: No annotations exist, no detection has been run - show start detection button
  if (totalDetectionsCount === 0 && showStartAssetDetection) {
    return <AiStartDetection />;
  }

  // Case 2: All annotations have been reviewed - show completion notification
  if (notReviewedCount === 0 && totalDetectionsCount > 0) {
    return (
      <Stack spacing={2}>
        <NotificationBox
          count={0}
          isExpanded={isExpanded}
          onToggle={toggleExpanded}
          onStartReview={handleStartReview}
          hasCurrentAnnotation={hasCurrentAnnotation}
          isComplete
        />
        {/* Show rerun button when feature is enabled and no task is running */}
        {canRerunAssetDetection && showStartAssetDetection && (
          <AiStartDetection />
        )}
      </Stack>
    );
  }

  // Case 3: There are annotations to review - show review notification
  return (
    <Stack spacing={2}>
      <NotificationBox
        count={notReviewedCount}
        isExpanded={isExpanded}
        onToggle={toggleExpanded}
        onStartReview={handleStartReview}
        hasCurrentAnnotation={hasCurrentAnnotation}
      />
      {/* Show rerun button when feature is enabled and no task is running */}
      {canRerunAssetDetection && showStartAssetDetection && (
        <AiStartDetection />
      )}
    </Stack>
  );
}

// Helper components

/** Props for the NotificationBox component */
interface NotificationBoxProps {
  /** Number of AI annotations to review */
  count: number;
  /** Whether the notification is expanded */
  isExpanded: boolean;
  /** Callback to toggle expanded state */
  onToggle(): void;
  /** Callback to start the review process */
  onStartReview(): void;
  /** Whether there is a current annotation being reviewed */
  hasCurrentAnnotation: boolean;
  /** Whether the review is complete (all annotations reviewed) */
  isComplete?: boolean;
}

/** @returns Notification box with review information */
const NotificationBox = memo(function NotificationBox({
  count,
  isExpanded,
  onToggle,
  onStartReview,
  hasCurrentAnnotation,
  isComplete = false,
}: NotificationBoxProps): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        ...AI_GRADIENT,
        borderRadius: 1,
        p: 2,
      }}
    >
      {isExpanded ? (
        <Expanded
          count={count}
          onToggle={onToggle}
          onStartReview={onStartReview}
          hasCurrentAnnotation={hasCurrentAnnotation}
          isComplete={isComplete}
        />
      ) : (
        <Collapsed count={count} onToggle={onToggle} isComplete={isComplete} />
      )}
    </Box>
  );
});

/** Props for the Header component */
interface HeaderProps {
  /** Number of AI annotations to review */
  count: number;
  /** Whether the notification is expanded */
  isExpanded: boolean;
  /** Callback to toggle expanded state */
  onToggle(): void;
  /** Whether the review is complete */
  isComplete?: boolean;
}

/** Props for the Collapsed component */
interface CollapsedProps {
  /** Number of AI annotations to review */
  count: number;
  /** Callback to toggle expanded state */
  onToggle(): void;
  /** Whether the review is complete */
  isComplete?: boolean;
}

/** Props for the Expanded component */
interface ExpandedProps {
  /** Number of AI annotations to review */
  count: number;
  /** Callback to toggle expanded state */
  onToggle(): void;
  /** Callback to start the review process */
  onStartReview(): void;
  /** Whether there is a current annotation being reviewed */
  hasCurrentAnnotation: boolean;
  /** Whether the review is complete */
  isComplete?: boolean;
}

/** @returns Header for the notification with expand/collapse toggle */
const Header = memo(function Header({
  count,
  isExpanded,
  onToggle,
  isComplete = false,
}: HeaderProps): JSX.Element {
  return (
    <Box
      component="div"
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        cursor: "pointer",
        "&:hover": {
          opacity: 0.9,
        },
      }}
    >
      {isComplete ? (
        <CheckCircleOutlineIcon
          sx={{ width: 20, height: 20, color: grey[0] }}
        />
      ) : (
        <InfoIcon sx={{ width: 20, height: 20, color: grey[0] }} />
      )}
      <FaroText variant="heading14" color={grey[0]}>
        {isComplete ? (
          "Review complete"
        ) : (
          <>
            <TranslateVar name="count">{count}</TranslateVar> AI annotation(s)
            to review
          </>
        )}
      </FaroText>
      {isExpanded ? (
        <ExpandLessIcon sx={{ color: grey[0] }} />
      ) : (
        <ExpandMoreIcon sx={{ color: grey[0] }} />
      )}
    </Box>
  );
});

/** @returns Collapsed state of the notification showing only the header */
const Collapsed = memo(function Collapsed({
  count,
  onToggle,
  isComplete = false,
}: CollapsedProps): JSX.Element {
  return (
    <Header
      count={count}
      isExpanded={false}
      onToggle={onToggle}
      isComplete={isComplete}
    />
  );
});

/** @returns Expanded state of the notification with description and action button */
const Expanded = memo(function Expanded({
  count,
  onToggle,
  onStartReview,
  hasCurrentAnnotation,
  isComplete = false,
}: ExpandedProps): JSX.Element {
  let buttonText = "Start review process";
  if (isComplete) {
    buttonText = "View results";
  } else if (hasCurrentAnnotation) {
    buttonText = "Continue review";
  }

  return (
    <Stack spacing={2}>
      <Header
        count={count}
        isExpanded
        onToggle={onToggle}
        isComplete={isComplete}
      />

      <FaroText variant="bodyM" sx={{ color: grey[0] }}>
        {isComplete
          ? "All AI-generated annotations have been reviewed"
          : "AI-generated annotations are waiting to be reviewed"}
      </FaroText>

      <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
        <FaroButton
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onStartReview();
          }}
          size="s"
          icon={<CheckCircleOutlineIcon sx={{ width: 18, height: 18 }} />}
          sx={{
            color: grey[0],
          }}
        >
          {buttonText}
        </FaroButton>
      </Stack>
    </Stack>
  );
});
